// Pure, immutable state model for the tiled window grid. Feature-agnostic: a
// window only knows its `type` + `entityId`; the feature decides how to render
// it. Layout is columns (left->right) each holding windows (top->bottom).
//
//   GridState { columns: Column[], activeWindowId, soloWindowId }
//   Column    { id, size, windows: Window[] }          size = width %
//   Window    { id, type, entityId, minimized, size, panes }  size = height %
//
// Sizes are snapshots for reload; the resize library drives live sizing and
// writes back via SET_*_SIZES. All updates are immutable to avoid the old app's
// stale-closure / in-place-mutation bugs.

export const GRID_VERSION = 1

function uid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  return 'w-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

export function createEmptyGrid() {
  return { columns: [], activeWindowId: null, soloWindowId: null }
}

// A grid that always starts with one (empty) window, so there is always an
// active window to receive the next selection. `entityType` is feature-supplied.
export function createInitialGrid(entityType) {
  const win = {
    id: uid(),
    type: entityType,
    entityId: null,
    minimized: false,
    size: 100,
    panes: null,
  }
  return {
    columns: [{ id: uid(), size: 100, windows: [win] }],
    activeWindowId: win.id,
    soloWindowId: null,
  }
}

function locate(state, id) {
  for (let c = 0; c < state.columns.length; c += 1) {
    const w = state.columns[c].windows.findIndex((win) => win.id === id)
    if (w !== -1) return { c, w }
  }
  return null
}

function totalWindows(state) {
  return state.columns.reduce((n, col) => n + col.windows.length, 0)
}

function countVisible(state) {
  return state.columns.reduce(
    (n, col) => n + col.windows.filter((w) => !w.minimized).length,
    0
  )
}

function firstVisibleWindowId(state) {
  for (const col of state.columns) {
    const win = col.windows.find((w) => !w.minimized)
    if (win) return win.id
  }
  return null
}

// Give every visible column an equal width (snapshot only).
function equalizeColumns(columns) {
  const visible = columns.filter((col) => col.windows.some((w) => !w.minimized))
  const size = visible.length ? 100 / visible.length : 100
  return columns.map((col) => ({ ...col, size }))
}

// Give every visible window in a column an equal height (snapshot only).
function equalizeWindows(col) {
  const visible = col.windows.filter((w) => !w.minimized)
  const size = visible.length ? 100 / visible.length : 100
  return {
    ...col,
    windows: col.windows.map((w) => (w.minimized ? w : { ...w, size })),
  }
}

function normalize(columns) {
  return equalizeColumns(columns.map(equalizeWindows))
}

// Add a new window (empty unless `entityId` given) and make it active. Windows
// are containers, so duplicates of the same entity are allowed. Capped at
// maxWindows (the caller shows the notice before dispatching).
function addWindow(state, { entityType, entityId = null, limits }) {
  const { maxWindows = 6, maxPerColumn = 3, maxColumns = 3 } = limits || {}
  if (totalWindows(state) >= maxWindows) return state

  const win = { id: uid(), type: entityType, entityId, minimized: false, size: 100, panes: null }
  let columns = state.columns.map((col) => ({ ...col, windows: [...col.windows] }))

  // Fill columns before stacking rows: open a new column until all maxColumns
  // exist, then add a row to the first column that still has room.
  let target
  if (columns.length < maxColumns) {
    columns.push({ id: uid(), size: 100, windows: [] })
    target = columns.length - 1
  } else {
    target = columns.findIndex((col) => col.windows.length < maxPerColumn)
    if (target === -1) {
      // Every column is at maxPerColumn: fall back to the emptiest.
      target = columns.reduce(
        (min, col, i, arr) => (col.windows.length < arr[min].windows.length ? i : min),
        0
      )
    }
  }

  columns[target] = { ...columns[target], windows: [...columns[target].windows, win] }
  return { ...state, columns: normalize(columns), activeWindowId: win.id, soloWindowId: null }
}

function setWindowEntity(state, id, entityId) {
  return {
    ...state,
    columns: state.columns.map((col) => ({
      ...col,
      windows: col.windows.map((w) => (w.id === id ? { ...w, entityId } : w)),
    })),
  }
}

// Load an entity into the active window (the last-focused visible window). Falls
// back to the first visible window, or opens a new window if somehow none exist.
function selectEntity(state, { entityType, entityId, limits }) {
  let targetId = state.activeWindowId
  const loc = targetId ? locate(state, targetId) : null
  const isVisible = loc && !state.columns[loc.c].windows[loc.w].minimized
  if (!isVisible) targetId = firstVisibleWindowId(state)
  if (!targetId) return addWindow(state, { entityType, entityId, limits })
  return { ...setWindowEntity(state, targetId, entityId), activeWindowId: targetId, soloWindowId: null }
}

function closeWindow(state, id) {
  if (!locate(state, id)) return state
  // At least one window must always remain (mirrors the minimize guard).
  if (totalWindows(state) <= 1) return state
  let columns = state.columns
    .map((col) => ({ ...col, windows: col.windows.filter((w) => w.id !== id) }))
    .filter((col) => col.windows.length > 0)
  columns = normalize(columns)
  return {
    columns,
    activeWindowId:
      state.activeWindowId === id ? firstVisibleWindowId({ columns }) : state.activeWindowId,
    soloWindowId: state.soloWindowId === id ? null : state.soloWindowId,
  }
}

function setMinimized(state, id, minimized) {
  if (!locate(state, id)) return state
  // At least one window must always stay visible.
  if (minimized && countVisible(state) <= 1) return state
  const columns = normalize(
    state.columns.map((col) => ({
      ...col,
      windows: col.windows.map((w) => (w.id === id ? { ...w, minimized } : w)),
    }))
  )
  let activeWindowId = state.activeWindowId
  if (minimized && state.activeWindowId === id) activeWindowId = firstVisibleWindowId({ columns })
  if (!minimized) activeWindowId = id
  return {
    ...state,
    columns,
    activeWindowId,
    soloWindowId: minimized && state.soloWindowId === id ? null : state.soloWindowId,
  }
}

function setColumnSizes(state, sizes) {
  return {
    ...state,
    columns: state.columns.map((col) =>
      sizes[col.id] != null ? { ...col, size: sizes[col.id] } : col
    ),
  }
}

function setWindowSizes(state, columnId, sizes) {
  return {
    ...state,
    columns: state.columns.map((col) =>
      col.id !== columnId
        ? col
        : {
            ...col,
            windows: col.windows.map((w) =>
              sizes[w.id] != null ? { ...w, size: sizes[w.id] } : w
            ),
          }
    ),
  }
}

function setPanes(state, id, panes) {
  return {
    ...state,
    columns: state.columns.map((col) => ({
      ...col,
      windows: col.windows.map((w) => (w.id === id ? { ...w, panes } : w)),
    })),
  }
}

export function gridReducer(state, action) {
  switch (action.type) {
    case 'ADD_WINDOW':
      return addWindow(state, action)
    case 'SELECT_ENTITY':
      return selectEntity(state, action)
    case 'SET_ENTITY':
      return setWindowEntity(state, action.id, action.entityId)
    case 'CLOSE':
      return closeWindow(state, action.id)
    case 'MINIMIZE':
      return setMinimized(state, action.id, true)
    case 'RESTORE':
      return setMinimized(state, action.id, false)
    case 'FOCUS':
      return state.activeWindowId === action.id ? state : { ...state, activeWindowId: action.id }
    case 'SET_COLUMN_SIZES':
      return setColumnSizes(state, action.sizes)
    case 'SET_WINDOW_SIZES':
      return setWindowSizes(state, action.columnId, action.sizes)
    case 'SET_PANES':
      return setPanes(state, action.id, action.panes)
    case 'SET_SOLO':
      return { ...state, soloWindowId: action.id }
    default:
      return state
  }
}
